// src/features/ats-sync/components/unlink-confirm-modal.tsx
"use client";

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalClose,
} from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import { useUnlinkJob } from "../hooks/use-ats-job-link";

interface UnlinkConfirmModalProps {
  jobId: string;
  connectorName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Point({
  icon,
  tint,
  children,
}: {
  icon: IconName;
  tint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <span
        className={`mt-px flex size-7 shrink-0 items-center justify-center rounded-lg ${tint}`}
      >
        <Icon name={icon} size={14} />
      </span>
      <p className="pt-1 text-[13px] leading-relaxed text-contrast/65">
        {children}
      </p>
    </div>
  );
}

/**
 * Confirms unlinking a job from its ATS. Spells out the three semantics of a
 * dormant link — the job keeps working locally, already-synced candidates
 * pause, and re-linking later is possible — so the action reads as reversible.
 */
export function UnlinkConfirmModal({
  jobId,
  connectorName,
  open,
  onOpenChange,
}: UnlinkConfirmModalProps) {
  const { mutateAsync, isPending } = useUnlinkJob(jobId);

  const handleConfirm = async () => {
    try {
      await mutateAsync();
    } catch (err) {
      // Keep the modal open on failure so the user can retry.
      const description =
        err instanceof Error ? err.message : "Please try again.";
      showToast.error("Couldn't unlink", { description });
      return;
    }
    showToast.success("Unlinked — working locally");
    onOpenChange(false);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader
          title={`Unlink from ${connectorName}?`}
          iconName="IconBrokenChainLink1"
        />
        <ModalBody>
          <div className="flex flex-col gap-3.5">
            <Point icon="IconCircleCheck" tint="bg-[#34D399]/10 text-[#34D399]">
              This job keeps working locally in Arbi —{" "}
              <span className="font-medium text-heading">
                nothing here is deleted
              </span>
              .
            </Point>
            <Point icon="IconPause" tint="bg-[#FFAF53]/10 text-[#FFAF53]">
              Candidates already synced from {connectorName}{" "}
              <span className="font-medium text-heading">pause</span> — no more
              updates flow in either direction.
            </Point>
            <Point
              icon="IconArrowsRepeatRightLeft"
              tint="bg-[#4C9AFF]/10 text-[#4C9AFF]"
            >
              You can{" "}
              <span className="font-medium text-heading">
                re-link to {connectorName}
              </span>{" "}
              later to resume syncing.
            </Point>
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalClose asChild>
            <Button variant="secondary" size="sm">
              Cancel
            </Button>
          </ModalClose>
          <SubmitButton
            type="button"
            variant="destructive"
            size="sm"
            onClick={handleConfirm}
            disabled={isPending}
            loading={isPending}
            loadingLabel="Unlinking"
          >
            Unlink
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
